/**
 * MediaProxy Component
 * Handles authenticated media fetching for WAHA API
 * Creates blob URLs for media that require X-Api-Key header
 */

import { useState, useEffect, memo, useCallback } from 'react';
import { ImageOff, AlertCircle, Loader2 } from 'lucide-react';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';
import { callWahaProxy } from '@/lib/services/wahaProxyService';
import { repairMediaBlob } from './mediaBytes';

interface MediaProxyProps {
  url: string;
  type: 'image' | 'video';
  className?: string;
  onClick?: () => void;
  alt?: string;
  timestamp?: string;
  isSticker?: boolean;
}

// Simple in-memory cache for blob URLs
export const blobCache = new Map<string, string>();

export const MediaProxy = memo(function MediaProxy({
  url,
  type,
  className = '',
  onClick,
  alt = 'Media',
  timestamp,
  isSticker = false,
}: MediaProxyProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const { wahaConfig } = useWhatsAppChatStore();

  const fetchMedia = useCallback(async () => {
    if (!url) {
      setLoading(false);
      setError(true);
      return;
    }

    // Check cache first
    const cached = blobCache.get(url);
    if (cached) {
      setBlobUrl(cached);
      setLoading(false);
      return;
    }

    // Check if URL is a data URL (already base64)
    if (url.startsWith('data:')) {
      setBlobUrl(url);
      setLoading(false);
      return;
    }

    // Check if URL needs authentication (WAHA media URL)
    const isWahaUrl = url.includes('/api/files/') || url.includes('/api/media/') ||
      (wahaConfig?.apiUrl && url.startsWith(wahaConfig.apiUrl));
    if (!isWahaUrl) {
      // Direct URL without auth
      setBlobUrl(url);
      setLoading(false);
      return;
    }

    try {
      // Extract path from WAHA URL and route through proxy
      const urlObj = new URL(url);
      const path = urlObj.pathname + urlObj.search;

      const response = await callWahaProxy({
        method: 'GET',
        path,
      });

      if (!response.ok) {
        // 404 è comune con NOWEB (media scaduti/non disponibili) — non loggare come errore
        if (response.status === 404) {
          setError(true);
          setLoading(false);
          return;
        }
        throw new Error(`HTTP ${response.status}`);
      }

      // Foto inviate da API: WAHA le salva con byte estranei in testa (vedi mediaBytes.ts).
      const blob = await repairMediaBlob(await response.blob());
      const objectUrl = URL.createObjectURL(blob);

      // Cache the blob URL
      blobCache.set(url, objectUrl);
      setBlobUrl(objectUrl);
    } catch (err) {
      console.error('[MediaProxy] Error fetching media:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [url, wahaConfig]);

  useEffect(() => {
    setLoading(true);
    setError(false);
    setBlobUrl(null);
    fetchMedia();

    // Cleanup blob URL on unmount (but keep in cache for reuse)
    return () => {
      // Don't revoke - we're caching for reuse
    };
  }, [fetchMedia]);

  if (loading) {
    return (
      <div className={`flex items-center justify-center bg-[rgba(55,53,47,0.08)] dark:bg-white/10 rounded-lg ${className || 'w-48 h-32'}`}>
        <Loader2 className="w-6 h-6 text-[#787774] animate-spin" />
      </div>
    );
  }

  if (error || !blobUrl) {
    return (
      <div className={`flex flex-col items-center justify-center bg-[rgba(55,53,47,0.08)] dark:bg-white/10 rounded-lg gap-2 ${className || 'w-48 h-32'}`}>
        <ImageOff className="w-6 h-6 text-[#787774]" />
        <span className="text-xs text-[#787774]">Impossibile caricare</span>
      </div>
    );
  }

  if (type === 'video') {
    return (
      <video
        src={blobUrl}
        controls
        className={`max-w-[280px] max-h-[200px] rounded-lg ${className}`}
        preload="metadata"
      />
    );
  }

  // Sticker - niente bordo, più piccolo
  if (isSticker) {
    return (
      <img
        src={blobUrl}
        alt={alt}
        className={`max-w-[150px] max-h-[150px] object-contain cursor-pointer ${className}`}
        onClick={onClick}
      />
    );
  }

  // Image - bordo sottile + overlay timestamp
  return (
    <div className="relative inline-block">
      <img
        src={blobUrl}
        alt={alt}
        className={`max-w-[420px] max-h-[320px] object-cover rounded-lg cursor-pointer border border-black/10 dark:border-white/10 ${className}`}
        onClick={onClick}
      />
      {timestamp && (
        <span className="absolute bottom-2 right-2 text-[11px] text-white bg-black/50 px-2 py-0.5 rounded shadow-sm">
          {timestamp}
        </span>
      )}
    </div>
  );
});

// Utility to clear cache (call on logout or memory pressure)
export function clearMediaCache() {
  blobCache.forEach((blobUrl) => {
    URL.revokeObjectURL(blobUrl);
  });
  blobCache.clear();
}
